import styled from "styled-components";
import { useState, useEffect } from "react";
import {
  Row,
  Col,
  Button,
  Space,
  Modal,
  Pagination,
  notification,
  Tag,
} from "antd";
import { PlusOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Card from "../../../Components/Backoffice/Card";
import Sidebar from "../../../Components/Backoffice/Sidebar";
import Header from "../../../Components/Backoffice/Header";
import Footer from "../../../Components/Backoffice/Footer";
import Title from "../../../Components/Backoffice/Title";

const Wrapper = styled.main`
  background-color: #e8e9f3;
  display: grid;
  grid-template-columns: 7.083vw 1fr;
  min-height: 100vh;

  .item__title {
    color: #494d7d;
    letter-spacing: 0px;
    font-size: 16px;
    font-weight: bold !important;
    line-height: 18px;
    text-align: left;
    width: fit-content;
  }
  .item__date {
    color: #ccc;
    letter-spacing: 0px;
    margin-top: 5px;
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
    width: fit-content;
    text-align: left;
  }

  .item__actions {
    display: flex;
    gap: 10px;
  }
`;

const Content = styled.div`
  padding: 1.771vw;
`;

const GridCards = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;

  .flex {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
  }

  .item {
    margin: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 100%;
    align-items: start;
  }
`;

export default function EditorDashboard() {
  document.title = "SPAP - Editores de conteúdo";
  const token = JSON.parse(localStorage.getItem("session_data"))?.token;

  const navigate = useNavigate();
  const [editors, setEditors] = useState([]);
  const [currentItems, setCurrentItems] = useState([]);
  const [itemOffset, setItemOffset] = useState(0);
  const itemsPerPage = 15;
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editorToDelete, setEditorToDelete] = useState(null);

  useEffect(() => {
    fetchEditors();
  }, []);

  const fetchEditors = async () => {
    try {
      const response = await axios.get("https://api.spap.pt/api/editors/all");
      const editors = response.data.editor;
      const apresentacoes = response.data.apresentacoes;

      const enrichedEditors = editors.map((editor) => {
        const usedIn = apresentacoes.filter((ap) => {
          return String(ap.descricao) === String(editor.id);
        });

        return {
          ...editor,
          usedInTitles: usedIn.map((ap) => ap.titulo),
          usedInCount: usedIn.length,
        };
      });
      setEditors(enrichedEditors);
      setCurrentItems(enrichedEditors.slice(0, itemsPerPage));
    } catch (err) {
      notification.error({ description: "Erro ao carregar os editors." });
    }
  };

  const handlePageChange = (page) => {
    const newOffset = (page - 1) * itemsPerPage;
    setItemOffset(newOffset);
    setCurrentItems(editors.slice(newOffset, newOffset + itemsPerPage));
  };

  const handleCreate = () => {
    navigate("/builder");
  };

  const confirmDelete = (editor) => {
    setEditorToDelete(editor);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      const token = JSON.parse(localStorage.getItem("session_data"))?.token;
      await axios.delete(
        `https://api.spap.pt/api/admin/editor/${editorToDelete.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const newEditors = editors.filter((e) => e.id !== editorToDelete.id);
      setEditors(newEditors);
      setCurrentItems(newEditors.slice(itemOffset, itemOffset + itemsPerPage));
      notification.success({ description: "Editor removido com sucesso!" });
    } catch (err) {
      notification.error({ description: "Erro ao remover o editor." });
    } finally {
      setDeleteModalOpen(false);
      setEditorToDelete(null);
    }
  };

  // Função utilitária
  const formatDate = (isoDate) => {
    if (!isoDate) return "";
    const date = new Date(isoDate);

    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0"); // meses começam do 0
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  return (
    <Wrapper>
      <div>
        <Sidebar />
      </div>
      <Content>
        <Header />
        <Row
          align="middle"
          justify="space-between"
          style={{ marginBottom: 20 }}
        >
          <Title>Editores de Conteúdo</Title>
          <Col>
            <Button
              onClick={handleCreate}
              style={{
                background: "#494D7D",
                color: "#fff",
                borderRadius: "6px",
                height: "40px",
                display: "flex",
                fontWeight: "bold",
                alignItems: "center",
                padding: "0 30px",
              }}
            >
              <PlusOutlined /> Novo Editor
            </Button>
          </Col>
        </Row>
        {currentItems.length > 0 && (
          <Row gutter={[16, 16]}>
            {currentItems.map((editor) => (
              <Col key={editor.id} xs={24} sm={12} md={8}>
                <Card
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%", // faz o card ocupar toda a altura da coluna
                  }}
                >
                  <Row justify="space-between" align="middle">
                    <Col className="item__title" span={24}>
                      {editor.name}
                    </Col>
                  </Row>

                  <div style={{ flex: 1, marginTop: 10 }}>
                    {editor.usedInTitles.map((title) => (
                      <Tag
                        key={title}
                        style={{
                          display: "block",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          maxWidth: "100%",
                          marginBottom: 8,
                        }}
                      >
                        <b>Template utilizado em Apresentações:</b> {title}
                      </Tag>
                    ))}
                    {editor.usedInTitles.length === 0 && (
                      <Tag style={{ display: "block", marginBottom: 8 }}>
                        Template não utilizado
                      </Tag>
                    )}
                  </div>

                  {/* Botões sempre na base */}
                  <div className="item__actions" style={{ marginTop: "auto" }}>
                    <Space>
                      <Button
                        type="default"
                        onClick={() => navigate(`/builder/${editor.id}`)}
                        style={{ fontWeight: "bold" }}
                        icon={<EditOutlined />}
                      >
                        Editar
                      </Button>
                      <Button
                        type="primary"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => confirmDelete(editor)}
                      >
                        Apagar
                      </Button>
                    </Space>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        )}
        <Pagination
          style={{ marginTop: 20 }}
          current={itemOffset / itemsPerPage + 1}
          total={editors.length}
          pageSize={itemsPerPage}
          onChange={handlePageChange}
        />
        <Footer />
        <Modal
          title="Confirmar remoção"
          open={deleteModalOpen}
          onOk={handleDelete}
          onCancel={() => setDeleteModalOpen(false)}
          okText="Sim"
          cancelText="Cancelar"
        >
          <p>
            Tem a certeza que quer remover o editor "{editorToDelete?.name}"?
          </p>
        </Modal>
      </Content>
    </Wrapper>
  );
}
